import { Party } from "../enums/Party";
import { FormikStepper } from "../navigation/FormikStepper";
import { FormikStep } from "../navigation/FormikStep";
import { EmployerPages } from "../enums/EmployerPages";
import EmployerStart from "../page/employer/EmployerStart";
import { array, object, string } from "yup";
import {
    accountNumberValidator,
    dateRangeValidator,
    lessThanEqualToCurrentDateValidator, requiredIfFieldNotChecked,
    requiredIfFieldNotPresent,
    validateBeneficiaryDesignation,
    validateDocumentContent,
    validateDocumentsTotalSize,
    validatePolicyNumber,
    validatePrivacyId,
    validateUnitNumber,
    validRoutingNumber,
} from "../../form/Validators";
import AboutEmployer from "../page/employer/AboutEmployer";
import AboutEmployee from "../page/employer/AboutEmployee";
import Occupation from "../page/employer/Occupation";
import Employment from "../page/employer/Employment";
import EmployerDocument from "../page/employer/EmployerDocument";
import Condition from "../page/employer/Condition";
import EmployerSummary from "../page/employer/EmployerSummary";
import React from "react";
import { gatherInitialValues, submitHandler } from "../../service/FormService";
import { BenefitType, findPartyChoicesFor } from "../enums/BenefitType";
import { PfmlEmployerFormValues } from "../pfml/PfmlEmployerFormValues";
import { WorkSchedule } from "../enums/WorkSchedule";
import EmployerFinished from "../page/employer/EmployerFinished";
import UseLinkError from "../page/employer/UseLinkError";
import AboutContactPerson from "../page/employer/AboutContactPerson";
import Payments from "../page/common/Payments";
import { useParams } from "react-router-dom";
import OtherIncome from "../page/employer/OtherIncome";
import Premiums from "../page/employer/Premiums";
import Earnings from "../page/employer/Earnings";
import { Relationship } from "../enums/Relationship";
import ProductQuestionnaire from "../page/common/ProductQuestionnaire";
import OtherLeave from "../page/employer/OtherLeave";
import Death from "../page/employer/Death";
import LifeBenefits from "../page/employer/LifeBenefits";
import Product from "../page/employer/Product";
import { EmployeePages } from "../enums/EmployeePages";
import AboutRelationship from "../page/common/AboutRelationship";

const Employer = () => {
    const params = useParams();
    const coverage = params.coverage as BenefitType;
    const claimId = params.claimId;
    const initialValues = gatherInitialValues(claimId, coverage, Party.EMPLOYER);

    const benefit = initialValues.benefit;
    if(!findPartyChoicesFor(benefit).includes(Party.EMPLOYER) && !claimId) {
        return <UseLinkError benefit={benefit}/>
    }
    return <FormikStepper initialValues={initialValues}
                          onSubmit={submitHandler}>
        <FormikStep label={EmployerPages.START}>
            <EmployerStart/>
        </FormikStep>
        <FormikStep label={EmployerPages.EMPLOYER} hideStep={(initialValues) => initialValues.employerContactName === undefined}>
            <AboutEmployer/>
        </FormikStep>
        <FormikStep label={EmployerPages.EMPLOYEE} hideStep={(initialValues) => initialValues.privacyId === undefined}
                    validationSchema={object({
                        employeeSearch: requiredIfFieldNotChecked('newEmployee', 'If you don\'t specify an employee, please check the "Can\'t find the employee you\'re looking for?" checkbox.',
                            string().matches(/^.+\(Date of birth: \d{2}\/\d{2}\/\d{4}, member ID: \d{9}\)$/gi, 'You must choose an employee from the list, or check the "Can\'t find the employee you\'re looking for?" checkbox.')),
                        policyNumber: validatePolicyNumber(),
                        unitNumber: validateUnitNumber(),
                        privacyId: validatePrivacyId(),
                        firstName: requiredIfFieldNotPresent('privacyId', 'If you don\'t specify a Principal member ID, please specify your employee\'s first name.'),
                        lastName: requiredIfFieldNotPresent('privacyId', 'If you don\'t specify a Principal member ID, please specify your employee\'s last name.'),
                        dateOfBirth: requiredIfFieldNotPresent('privacyId', 'If you don\'t specify a Principal member ID, please specify your employee\'s date of birth.'),
                    })}>
            <AboutEmployee/>
        </FormikStep>
        <FormikStep label={EmployerPages.BENEFITS} hideStep={(initialValues) => initialValues.deceased === undefined}
                    validate={(values) => {
                        if(!(values.life || values.disability || values.pfml || values.hospitalIndemnity || values.accident || values.criticalIllness)) {
                            return {
                                field: 'Please specify at least one benefit.'
                            }
                        }
                    }}>
            <ProductQuestionnaire/>
        </FormikStep>
        <FormikStep label={EmployeePages.RELATIONSHIP} hideStep={(initialValues, values) => initialValues.deceasedFirstName === undefined || values.genericRelationship === Relationship.EMPLOYEE}>
            <AboutRelationship/>
        </FormikStep>
        <FormikStep label={EmployerPages.CONTACT} hideStep={(initialValues) => initialValues.contactName === undefined}>
            <AboutContactPerson/>
        </FormikStep>
        <FormikStep label={EmployerPages.OCCUPATION} hideStep={(initialValues) => initialValues.jobTitle === undefined}>
            <Occupation/>
        </FormikStep>
        <FormikStep label={EmployerPages.EMPLOYMENT}
                    hideStep={(initialValues) => initialValues.dateOfHire === undefined}
                    validationSchema={object({
                        dateLastWorked: dateRangeValidator('dateOfHire'),
                        dateReturnedToWork: dateRangeValidator('dateLastWorked'),
                        datePremiumsPaidThrough: lessThanEqualToCurrentDateValidator()
                    })} validate={(values) => {
            if (!values.workSchedule) {
                return;
            }
            const firstWeeklyWorkSchedule = values.weeklyWorkSchedule?.[0];
            if ([WorkSchedule.REGULAR, WorkSchedule.ROTATING].includes(values.workSchedule)
                && [firstWeeklyWorkSchedule.sunday, firstWeeklyWorkSchedule.monday, firstWeeklyWorkSchedule.tuesday,
                    firstWeeklyWorkSchedule.wednesday, firstWeeklyWorkSchedule.thursday, firstWeeklyWorkSchedule.friday,
                    firstWeeklyWorkSchedule.saturday].every(item => !item)) {
                return {
                    weeklyWorkSchedule: 'Please specify at least one work schedule.'
                }
            }
        }}>
            <Employment/>
        </FormikStep>
        <FormikStep
            label={EmployerPages.DEATH}
            hideStep={(initialValues) => initialValues?.dateOfDeath === undefined}>
            <Death/>
        </FormikStep>
        <FormikStep
            label={EmployerPages.LIFE_BENEFITS}
            hideStep={(initialValues) => initialValues?.benefits === undefined}>
            <LifeBenefits/>
        </FormikStep>
        <FormikStep
            label={EmployerPages.OTHER_LEAVE}
            hideStep={(initialValues) => initialValues.priorDisability === undefined && initialValues.priorPaidFamilyMedicalLeave === undefined}>
            <OtherLeave/>
        </FormikStep>
        <FormikStep label={EmployerPages.CONDITION} hideStep={(initialValues) => initialValues.workRelated === undefined}>
            <Condition/>
        </FormikStep>
        <FormikStep label={EmployerPages.PRODUCT} hideStep={(initialValues) => initialValues.product === undefined}>
            <Product/>
        </FormikStep>
        <FormikStep
            label={EmployerPages.EARNINGS}
            hideStep={(initialValues) => initialValues.baseWage === undefined
                && initialValues.bonusesOrCommissions === undefined
                && (initialValues as PfmlEmployerFormValues).earningsQuarters === undefined}>
            <Earnings/>
        </FormikStep>
        <FormikStep
            label={EmployerPages.PREMIUMS}
            hideStep={(initialValues, values) => (initialValues.premiumsPaidThroughDeath === undefined || Relationship.EMPLOYEE !== values.relationship)
                && initialValues.premiumTaxation === undefined}>
            <Premiums/>
        </FormikStep>
        <FormikStep
            label={EmployerPages.OTHER_INCOME}
            hideStep={(initialValues, values) => initialValues.payAfterDateLastWorked === undefined}
            validationSchema={object({
                fullPayAfterDateLastWorkedEndDate: dateRangeValidator('dateLastWorked'),
                payTypesAfterDateLastWorked: array().when('payAfterDateLastWorked', {
                    is: (value: string) => value !== undefined && value === 'Yes',
                    then: array().of(
                        object({
                            percentagePaid: requiredIfFieldNotPresent('totalHoursPaid', 'If you don\'t specify the total hours paid, please specify the percentage paid for the given pay type.')
                        })
                    ),
                    otherwise: array().notRequired()
                })
            })}>
            <OtherIncome/>
        </FormikStep>
        <FormikStep
            label={EmployerPages.PAYMENTS}
            hideStep={(initialValues, values) => {
                return (initialValues as PfmlEmployerFormValues).employerPaymentPreference === undefined
                || !values.employerReimbursement || 'No' === values.employerReimbursement
            }}
            validationSchema={object({
                employerRoutingNumber: validRoutingNumber(Party.EMPLOYER),
                employerAccountNumber2: accountNumberValidator(Party.EMPLOYER),
            })}>
            <Payments/>
        </FormikStep>
        <FormikStep
            label={EmployerPages.DOCUMENT}
            hideStep={(initialValues) => initialValues.documents === undefined}
            validationSchema={object({
                documents: validateDocumentContent(),
            })}
            validate={(values) => {
                return {
                    ...validateDocumentsTotalSize(values),
                    ...validateBeneficiaryDesignation(values)
                }
            }}>
            <EmployerDocument/>
        </FormikStep>
        <FormikStep label={EmployerPages.SUMMARY}>
            <EmployerSummary/>
        </FormikStep>
        <FormikStep label={EmployerPages.FINISH}>
            <EmployerFinished/>
        </FormikStep>
    </FormikStepper>
}

export default Employer;